import { Table, Text } from '@mantine/core';

import {
  converTransactionsToTable,
  convertTransactionFullDate,
  convertTransactionShortDate,
} from '../utils/helpers/converter';
import { CustomTable, CustomTableMobile, useResponsive } from '@/shared';
import { transactionsTypeTitles, transactionsStatusTitles } from '../utils/const/common';

import type { TTransaction } from '../store/types';

const isEqualAmounts = (paidAmount: string, requestAmount: string) => Number(paidAmount) === Number(requestAmount);

const getAmountWithCurrency = (amount: string | null, currency: string) => amount === null
  ? '-'
  : `${amount} ${currency}`;

const renderPaidAmount = (item: TTransaction) => {
  if (item.paidAmount === null) return '-';

  return (
    <Text span inherit>
      {item.paidAmount}
      {item.requestAmount !== null && !isEqualAmounts(item.paidAmount, item.requestAmount) && (
        <>
          &nbsp;
          <Text span c="red" inherit>({item.requestAmount})</Text>
        </>
      )}
      &nbsp;
      {item.currency}
    </Text>
  );
};

const getTransactionMethod = (item: TTransaction) => {
  if (item.methodId === null || item.methodName === null) return '-';

  const paymentSystemName = item.paymentSystemName ? `[${item.paymentSystemName}]` : '';

  return `(${item.methodId}) ${item.methodName} ${paymentSystemName}`;
};

type Props = {
  items: TTransaction[];
  isLoading?: boolean;
  itemsCount?: number;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
};

export const TransactionsTable = (props: Props) => {
  const { items } = props;
  const { isDesktop } = useResponsive();

  const titles = [
    'ID транзакции',
    'Создан',
    'ID магазина',
    'Метод',
    'Тип',
    'Статус',
    'Зачислено в магазин',
    'Валюта',
    'Сумма заявки',
    'Оплаченная сумма',
    'Баланс (до)',
    'Баланс (после)',
    'ID платежа или вывода',
  ];

  const rows = converTransactionsToTable(items).map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>{item.id}</Table.Td>
      <Table.Td>{item.created}</Table.Td>
      <Table.Td>{item.shopId}</Table.Td>
      <Table.Td>{getTransactionMethod(item)}</Table.Td>
      <Table.Td>{transactionsTypeTitles[item.type]}</Table.Td>
      <Table.Td>{transactionsStatusTitles[item.status]}</Table.Td>
      <Table.Td>{item.amount}</Table.Td>
      <Table.Td>{item.currency}</Table.Td>
      <Table.Td>{item.requestAmount ?? '-'}</Table.Td>
      <Table.Td>{item.paidAmount ?? '-'}</Table.Td>
      <Table.Td>{item.balanceBefore}</Table.Td>
      <Table.Td>{item.balanceAfter}</Table.Td>
      <Table.Td>{item.requestId ?? '-'}</Table.Td>
    </Table.Tr>
  ));

  const rowsMobile = items.map((item) => ({
    key: item.id,
    items: [
      { name: 'ID платежа или вывода', value: item.requestId ?? '-' },
      { name: 'Оплаченная сумма', value: renderPaidAmount(item) },
      { name: 'Статус', value: `${transactionsStatusTitles[item.status]} ${convertTransactionShortDate(item.created)}` },
      // { name: 'Время', value: convertTransactionShortDate(item.created) },
      { name: 'ID транзакции', value: item.id },
      { name: 'Сумма заявки', value: getAmountWithCurrency(item.requestAmount, item.currency) },
      { name: 'Оплаченная сумма', value: getAmountWithCurrency(item.paidAmount, item.currency) },
      { name: 'Создан', value: convertTransactionFullDate(item.created) },
      { name: 'ID магазина', value: item.shopId },
      { name: 'Метод', value: <Text span inherit ta={'right'}>{getTransactionMethod(item)}</Text> },
      { name: 'Тип', value: transactionsTypeTitles[item.type] },
      { name: 'Зачислено в магазин', value: `${item.amount} ${item.currency}` },
      // { name: 'Валюта', value: item.currency },
      { name: 'Баланс (до)', value: `${item.balanceBefore} ${item.currency}` },
      { name: 'Баланс (после)', value: `${item.balanceAfter} ${item.currency}` },
    ],
  }));

  return isDesktop
    ? (
      <CustomTable
        titles={titles}
        rows={rows}
        {...props}
      />
    )
    : (
      <CustomTableMobile
        rows={rowsMobile}
        headerPointsCount={3}
        {...props}
      />
    );
};
